import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SYMPTOM_KEYS } from '@toxofollow/shared';
import { api } from '../../lib/api.js';
import { useI18n } from '../../lib/i18n.js';
import { Card, Button, Field, inputClass, Badge } from '../../components/ui.js';
import { Textarea, clientId, QueryState } from '../../components/form.js';
import { ReportUpload } from '../../components/ReportUpload.js';
import { DayPager, useDayParam } from '../../components/DayPager.js';

// A focused subset of symptoms parents commonly note.
const PARENT_SYMPTOMS = SYMPTOM_KEYS.filter((s) => s !== 'other');

interface Question { id: string; text: string; answered: boolean; answer: string | null }

export function ParentJournal() {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  // The Journal writes for ONE day. It defaults to today but honours ?date= so
  // the day view can hand off a past day for correction.
  const [day, setDay, bounds, today] = useDayParam();
  const reportTitle = locale === 'en' ? 'Send a report to the doctor' : 'Envoyer un compte rendu au médecin';

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold text-slate-800">{t('weekly.observations')}</h1>
      <DayPager day={day} bounds={bounds} today={today} onChange={setDay} />
      <DoctorNotesCard />
      <GrowthForm />
      <CheckInForm />
      <ObservationForm />
      <Card title={reportTitle}>
        <ReportUpload
          onDone={() => {
            void qc.invalidateQueries({ queryKey: ['intake'] });
          }}
        />
      </Card>
      <QuestionsCard onChanged={() => qc.invalidateQueries({ queryKey: ['questions'] })} />
    </div>
  );

  function Saved({ show }: { show: boolean }) {
    return show ? <span className="text-ok text-sm ml-2">✓ {t('common.synced')}</span> : null;
  }

  // Read-only notes the doctor has shared with the family.
  function DoctorNotesCard() {
    interface DocNote { id: string; noteDate: string; category: string; title: string | null; text: string }
    const q = useQuery({ queryKey: ['clinicalNotes'], queryFn: () => api.get<{ notes: DocNote[] }>('/clinical-notes') });
    const notes = q.data?.notes ?? [];
    if (!q.isLoading && notes.length === 0) return null; // nothing shared yet → hide the card
    return (
      <Card title={t('notes.fromDoctor')}>
        <QueryState isLoading={q.isLoading} isError={q.isError} error={q.error} isEmpty={notes.length === 0} emptyMessage="—" errorFallback={t('common.error')}>
          <ul className="space-y-3">
            {notes.map((n) => (
              <li key={n.id} className="border-b border-slate-100 last:border-0 pb-2 last:pb-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-slate-400">{n.noteDate}</span>
                  <Badge tone="info">{t(`notes.cat.${n.category}`)}</Badge>
                  {n.title && <span className="text-sm font-medium text-slate-800">{n.title}</span>}
                </div>
                <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{n.text}</p>
              </li>
            ))}
          </ul>
        </QueryState>
      </Card>
    );
  }

  function GrowthForm() {
    const [weight, setWeight] = useState('');
    const [hc, setHc] = useState('');
    const m = useMutation({
      mutationFn: () =>
        api.post('/growth', {
          date: day,
          weightGrams: weight || undefined,
          weightSource: 'parent',
          headCircumferenceCm: hc || undefined,
        }),
      onSuccess: () => {
        setWeight('');
        setHc('');
        void qc.invalidateQueries({ queryKey: ['growth'] });
        void qc.invalidateQueries({ queryKey: ['trends'] });
      },
    });
    return (
      <Card title={`${t('growth.weight')} / ${t('growth.headCircumference')}`}>
        <div className="grid grid-cols-2 gap-3">
          <Field label={`${t('growth.weight')} (g)`}>
            <input className={inputClass} inputMode="decimal" value={weight} onChange={(e) => setWeight(e.target.value)} />
          </Field>
          <Field label={`${t('growth.headCircumference')} (cm)`}>
            <input className={inputClass} inputMode="decimal" value={hc} onChange={(e) => setHc(e.target.value)} />
          </Field>
        </div>
        <Button onClick={() => m.mutate()} disabled={m.isPending || (!weight && !hc)}>{t('common.save')}</Button>
        <Saved show={m.isSuccess} />
        <p className="text-xs text-slate-400 mt-2">{t('weight.unverified')} — {t('action.askDoctor')}</p>
      </Card>
    );
  }

  function CheckInForm() {
    interface CheckIn { date: string; maxTemperatureC: string | null; wetDiapers: number | null; stools: number | null; symptoms: string[] }
    // One check-in per day: paging to a day must show what is stored there, so
    // the form pre-fills and a save CORRECTS that day rather than appending.
    const existingQ = useQuery({ queryKey: ['checkins'], queryFn: () => api.get<{ checkIns: CheckIn[] }>('/checkins') });
    const existing = existingQ.data?.checkIns.find((c) => c.date === day) ?? null;

    const [temp, setTemp] = useState('');
    const [diapers, setDiapers] = useState('');
    const [stools, setStools] = useState('');
    const [symptoms, setSymptoms] = useState<string[]>([]);

    useEffect(() => {
      setTemp(existing?.maxTemperatureC ?? '');
      setDiapers(existing?.wetDiapers == null ? '' : String(existing.wetDiapers));
      setStools(existing?.stools == null ? '' : String(existing.stools));
      setSymptoms(existing?.symptoms ?? []);
      // Re-seed whenever the day changes or that day's stored row arrives.
    }, [day, existing?.date, existing?.maxTemperatureC, existing?.wetDiapers, existing?.stools]);

    const m = useMutation({
      mutationFn: () =>
        api.post('/checkins', {
          date: day,
          maxTemperatureC: temp || undefined,
          wetDiapers: diapers ? Number(diapers) : undefined,
          stools: stools ? Number(stools) : undefined,
          symptoms,
          clientId: clientId('ci'),
        }),
      // Do NOT clear the fields — this is an editable day record now, so it
      // should keep showing what is stored. Refetch so a correction is visible.
      onSuccess: () => { void qc.invalidateQueries({ queryKey: ['checkins'] }); },
    });
    const toggle = (s: string) => setSymptoms((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
    return (
      <Card title={t('checkin.temperature')}>
        <div className="grid grid-cols-3 gap-3">
          <Field label={t('checkin.temperature')}>
            <input className={inputClass} inputMode="decimal" value={temp} onChange={(e) => setTemp(e.target.value)} />
          </Field>
          <Field label={t('checkin.wetDiapers')}>
            <input className={inputClass} inputMode="numeric" value={diapers} onChange={(e) => setDiapers(e.target.value)} />
          </Field>
          <Field label={t('checkin.stools')}>
            <input className={inputClass} inputMode="numeric" value={stools} onChange={(e) => setStools(e.target.value)} />
          </Field>
        </div>
        <div className="flex flex-wrap gap-2 my-2">
          {PARENT_SYMPTOMS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => toggle(s)}
              className={`px-2 py-1 rounded-full text-xs border ${symptoms.includes(s) ? 'bg-warn text-white border-warn' : 'bg-white text-slate-600 border-slate-300'}`}
            >
              {t(`symptom.${s}`)}
            </button>
          ))}
        </div>
        <Button onClick={() => m.mutate()} disabled={m.isPending}>{t('common.save')}</Button>
        <Saved show={m.isSuccess} />
      </Card>
    );
  }

  function ObservationForm() {
    const [text, setText] = useState('');
    const m = useMutation({
      mutationFn: () => api.post('/observations', { date: day, text, clientId: clientId('obs') }),
      onSuccess: () => { setText(''); void qc.invalidateQueries({ queryKey: ['observations'] }); },
    });
    return (
      <Card title={t('weekly.observations')}>
        <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder={t('weekly.observations')} />
        <div className="mt-2">
          <Button onClick={() => m.mutate()} disabled={m.isPending || !text.trim()}>{t('common.save')}</Button>
          <Saved show={m.isSuccess} />
        </div>
      </Card>
    );
  }

  function QuestionsCard({ onChanged }: { onChanged: () => void }) {
    const [text, setText] = useState('');
    const q = useQuery({ queryKey: ['questions'], queryFn: () => api.get<{ questions: Question[] }>('/questions') });
    const m = useMutation({
      mutationFn: () => api.post('/questions', { text, clientId: clientId('q') }),
      onSuccess: () => { setText(''); onChanged(); },
    });
    return (
      <Card title={t('weekly.questions')}>
        <div className="flex gap-2 mb-3">
          <input className={inputClass} value={text} onChange={(e) => setText(e.target.value)} placeholder={t('weekly.questions')} />
          <Button onClick={() => m.mutate()} disabled={m.isPending || !text.trim()}>+</Button>
        </div>
        <QueryState isLoading={q.isLoading} isError={q.isError} error={q.error} isEmpty={(q.data?.questions.length ?? 0) === 0} emptyMessage="—" errorFallback={t('common.error')}>
          <ul className="space-y-2">
            {q.data?.questions.map((item) => (
              <li key={item.id} className="text-sm flex items-start gap-2">
                <Badge tone={item.answered ? 'ok' : 'info'}>{item.answered ? '✓' : '…'}</Badge>
                <div>
                  <div>{item.text}</div>
                  {item.answered && item.answer && <div className="text-slate-500">→ {item.answer}</div>}
                </div>
              </li>
            ))}
          </ul>
        </QueryState>
      </Card>
    );
  }
}
